import { isPlayerConnect, type ConnectionInstance, type PlayerConnect } from "../connection/ConnectionInstance";
import { SingleplayerConnectionInstance } from "../connection/SingleplayerConnectionInstance";
import { GameInstance } from "./GameInstance";
import type { GameModel } from "./GameModel";
import { InputManager, MappedKeys } from "../inputs/InputManager";
import { MouseManager } from "../inputs/MouseManager";
import { KeyboardListener } from "../inputs/KeyboardListener";
import AssetLoader from "../loader/AssetLoader";
import { PlayerInput } from "yage/schemas/core/PlayerInput";
import { UIService } from "../ui/UIService";
import { HistoryConnectionInstance } from "yage/connection/HistoryConnectionInstance";
import type {
  PeerMultiplayerInstanceOptions} from "yage/connection/PeerMultiplayerInstance";
import {
  isPeerMultiplayerInstanceOptions,
  PeerMultiplayerInstance
} from "yage/connection/PeerMultiplayerInstance";
import type {
  SocketIoMultiplayerInstanceOptions} from "yage/connection/SocketIoMultiplayerInstance";
import {
  isSocketIoMultiplayerInstanceOptions,
  SocketIoMultiplayerInstance
} from "yage/connection/SocketIoMultiplayerInstance";
import { E2EConnectionInstance } from "yage/connection/E2EConnectionInstance";
import { E2EBridge } from "yage/testing/E2EBridge";
import { ensureMobileFullscreenButton } from "./mobileFullscreen";

type QuickStartMappedKeys = Record<string, string>;

const keyboardKeysFromMappedKeys = (mappedKeys: QuickStartMappedKeys): string[] => {
  return [...new Set(Object.values(mappedKeys))];
};

export type QuickStartOptions<T> = {
  gameName: string;
  connection?: "SOCKET" | "PEER" | "SINGLEPLAYER" | "COOP" | "REPLAY" | ConnectionInstance<T>;
  buildWorld?: (gameModel: GameModel, firstPlayerConfig: T) => void;
  onPlayerJoin: (gameModel: GameModel, playerId: string, playerConfig: T) => number;
  onPlayerLeave?: (gameModel: GameModel, playerId: string) => void;
  dt?: number;
  roomId?: string;
  seed?: string;
  preload?: (uiService: UIService) => Promise<void>;
  executionMode?: "realtime" | "step";
  mappedKeys?: QuickStartMappedKeys;
};

export async function QuickStart<T = null>(
  options: QuickStartOptions<T> & { connection: "PEER" },
  playerConfig: PlayerConnect<T>,
  multiplayerConfig: PeerMultiplayerInstanceOptions<T>,
): Promise<GameInstance<T>>;
export async function QuickStart<T = null>(
  options: QuickStartOptions<T> & { connection: "SOCKET" },
  playerConfig: PlayerConnect<T>,
  multiplayerConfig: SocketIoMultiplayerInstanceOptions<T>,
): Promise<GameInstance<T>>;
export async function QuickStart<T = null>(
  options: QuickStartOptions<T> & { connection: "SINGLEPLAYER" },
  playerConfig?: T,
): Promise<GameInstance<T>>;
export async function QuickStart<T = null>(
  options: QuickStartOptions<T> & { connection: "REPLAY" },
  playerConfig?: T,
): Promise<GameInstance<T>>;
export async function QuickStart<T = null>(
  {
    roomId = "QuickStart",
    seed = "QuickStart",
    connection = "SINGLEPLAYER",
    executionMode,
    buildWorld = () => {},
    onPlayerJoin,
    onPlayerLeave = (gameModel: GameModel, playerId: string) => {
      const players = gameModel.getComponentActives("PlayerInput");
      const player = players.find((p) => {
        const playerInput = gameModel.getTypedUnsafe(PlayerInput, p);
        return playerInput.pid === playerId;
      });
      if (player) {
        gameModel.removeEntity(player);
      }
    },
    preload = async () => {
      await AssetLoader.getInstance().load();
    },
    mappedKeys = MappedKeys,
  }: QuickStartOptions<T>,
  playerConfig?: T | PlayerConnect<T>,
  multiplayerConfig?: PeerMultiplayerInstanceOptions<T> | SocketIoMultiplayerInstanceOptions<T>,
) {
  let inputManager: InputManager;
  const unsubscribes: (() => void)[] = [];

  const initializeInputManager = () => {
    inputManager = new InputManager();
    const keyboardListener = new KeyboardListener(inputManager);
    keyboardListener.init(keyboardKeysFromMappedKeys(mappedKeys));
    unsubscribes.push(() => keyboardListener.destroy());
    // Bridge cursor position/buttons into input state so systems can read
    // it via getPointerState (PlayerInput carries the mouse fields).
    new MouseManager(inputManager);
  };

  const initializeConnection = (
    connection: "SOCKET" | "PEER" | "SINGLEPLAYER" | "COOP" | "REPLAY" | ConnectionInstance<T>,
    playerConfig?: T | PlayerConnect<T>,
  ): ConnectionInstance<T> => {
    if (typeof connection !== "string") {
      return connection;
    }
    if (connection === "REPLAY") {
      return new HistoryConnectionInstance<T>();
    }
    if (connection === "SINGLEPLAYER") {
      return new SingleplayerConnectionInstance<T>(
        inputManager,
        isPlayerConnect<T>(playerConfig) ? playerConfig.config : playerConfig,
      );
    }
    if (connection === "PEER") {
      if (!isPlayerConnect<T>(playerConfig)) {
        throw new Error("Player connect is required for multiplayer");
      }
      if (!isPeerMultiplayerInstanceOptions(multiplayerConfig)) {
        throw new Error("Multiplayer config is required for multiplayer");
      }
      return new PeerMultiplayerInstance<T>(playerConfig, inputManager, multiplayerConfig);
    }
    if (connection === "SOCKET") {
      if (!isPlayerConnect<T>(playerConfig)) {
        throw new Error("Player connect is required for multiplayer");
      }
      if (!isSocketIoMultiplayerInstanceOptions(multiplayerConfig)) {
        throw new Error("Multiplayer config is required for multiplayer");
      }
      return new SocketIoMultiplayerInstance<T>(playerConfig, inputManager, multiplayerConfig);
    }
    throw new Error("Connection type not supported");
  };

  const initializeGameInstance = (connection: ConnectionInstance<T>) => {
    return new GameInstance({
      connection: connection,
      uiService: true,
      buildWorld,
      onPlayerJoin,
      onPlayerLeave,
      executionMode,
    });
  };

  await preload(UIService.getInstance());

  initializeInputManager();

  // Detect E2E mode via URL parameter
  const isE2E = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("e2e") === "true";

  if (isE2E) {
    // Override connection with E2EConnectionInstance for deterministic headless testing
    const e2eConnection = new E2EConnectionInstance<T>(inputManager!);
    await e2eConnection.connect();
    const instance: GameInstance<T> = initializeGameInstance(e2eConnection);
    await instance.initializeRoom(roomId, seed);

    // Stop the ticker to allow manual frame stepping
    // @ts-ignore - accessing protected ticker
    if (instance.ticker) {
      // @ts-ignore
      instance.ticker.stop();
    }

    // Inject the E2EBridge as a global for Playwright RPC access
    const bridge = new E2EBridge(instance, inputManager!);
    bridge.ready = true;
    // @ts-ignore
    window.__YAGE_E2E__ = bridge;

    return instance;
  }

  if (typeof connection === "string") {
    connection = initializeConnection(connection, playerConfig);
  }
  await connection.connect();

  // A replay connection carries the seed the world was originally built
  // with; rebuilding with any other seed desyncs the replay from frame 1.
  const replayHistory =
    connection instanceof HistoryConnectionInstance ? connection.replayHistory : undefined;

  const instance: GameInstance<T> = initializeGameInstance(connection);
  await instance.initializeRoom(roomId, replayHistory?.seed ?? seed);
  ensureMobileFullscreenButton();

  return instance;
}
